/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-empty-function */
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Group, Space } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useModals } from '@mantine/modals';
import { EntityTreatmentType, EntityType } from '../../../../../../../models/core/entities.type';
import TreatmentFormViewActions from './TreatmentFormViewActions';
import TreatmentFormAdd from './TreatmentFormSearch';
import { Feature, SessionStorageKey } from '../../../../../../../utils/constants.utils';
import PageSection from '../../../../../../../components/core/PageSection/PageSection';
import { Action } from '../../../../../../../models/core/core.type';

type FormViewProps = {
  entity: EntityType | null;
};

const TreatmentFormView = forwardRef((props: FormViewProps, ref) => {
  const modals = useModals();

  const [data, setData] = useState<EntityTreatmentType[]>(
    props.entity?.residuoTratamentos.map((x) => {
      return {
        ...x,
        action: Action.Nothing,
        id: x.idEntidadeResiduoTratamento?.toString() || '',
      };
    }) || []
  );

  const columns: ColumnsType<any> = [
    {
      title: 'Tratamento',
      key: 'residuoTratamento',
      dataIndex: 'residuoTratamento',
      sorter: (a: EntityTreatmentType, b: EntityTreatmentType) =>
        a.residuoTratamento.localeCompare(b.residuoTratamento),
    },
    {
      title: 'Descrição',
      key: 'residuoTratamentoDescricao',
      sorter: (a: EntityTreatmentType, b: EntityTreatmentType) =>
        (a.residuoTratamentoDescricao || '').localeCompare(b.residuoTratamentoDescricao || ''),
      render: (row: EntityTreatmentType) => <div>{row?.residuoTratamentoDescricao || '-'}</div>,
    },
    {
      title: 'Ações',
      width: '100px',
      render: (row: EntityTreatmentType) => (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <TreatmentFormViewActions item={row} confirmActionResult={confirmActionResult} />
      ),
    },
  ];

  const confirmActionResult = (items: EntityTreatmentType[], action: string, confirmed: boolean) => {
    if (!confirmed) {
      return;
    }

    let dataItemIndex;
    let dataItem;

    switch (action) {
      case 'excluir':
        dataItemIndex = data.findIndex((x) => x.id === items[0]?.id);
        if (dataItemIndex > -1) {
          dataItem = data[dataItemIndex];
          if (dataItem.idEntidadeResiduoTratamento) {
            data[dataItemIndex] = { ...dataItem, action: Action.Delete };
          } else {
            data.splice(dataItemIndex, 1);
          }
          setData(JSON.parse(JSON.stringify(data)));
        }
        break;
      case 'adicionar':
        modals.openModal({
          title: `Tratamento - Adicionar`,
          size: '60%',
          closeOnClickOutside: false,
          children: (
            <TreatmentFormAdd
              items={data}
              idEntidade={props.entity?.idEntidade}
              callback={confirmActionResult}
            />
          ),
        });
        break;
      case 'callback':
        modals.closeAll();
        if (items.length > 0) {
          setData(data.concat(items));
        }
        break;
      default:
        break;
    }
  };

  useImperativeHandle(ref, () => ({
    validate(): EntityTreatmentType[] {
      return data;
    },
    clear() {
      setData([]);
    },
  }));

  useEffect(() => {
    const tempEntity = JSON.parse(sessionStorage.getItem(SessionStorageKey.TempEntity) || 'null');
    if (tempEntity) {
      setData(tempEntity.residuoTratamentos);
    }
  }, []);

  return (
    <div>
      <Group justify="space-between">
        <PageSection size="lg" color={Feature.Reference.Entity.color} label="Tratamentos" text="" />
        <Button
          color="primary"
          leftSection={<IconPlus size={18} />}
          onClick={() => {
            confirmActionResult(data, 'adicionar', true);
          }}
        >
          Adicionar
        </Button>
      </Group>
      <Space h="xs" />
      <Table
        showSorterTooltip={false}
        dataSource={data.filter((x) => x.action !== Action.Delete)}
        columns={columns}
        rowKey={(item: EntityTreatmentType) => item.id || 0}
        pagination={{
          pageSizeOptions: [10, 25, 50],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} - ${range[1]} de ${total} resultado(s)`,
        }}
      />
    </div>
  );
});

export default TreatmentFormView;
